/**
 * Deterministic tiebreak dice — one die per tied player, TOSSED from that player's
 * seat onto the felt during the paced tiebreak's "throw" beat (driven by the store
 * `tiebreakBeat`; see Tiebreak.tsx + TiebreakDirector). The SERVER decides the
 * outcome; the panels show the authoritative numbers, so these dice are a
 * deterministic cosmetic tumble (CLAUDE.md rule 5: fixed timestep, server seed —
 * `timeStep:'vary'` forbidden).
 */

import { useMemo } from 'react';
import * as THREE from 'three';
import { Physics, RigidBody, CuboidCollider, type RapierRigidBody } from '@react-three/rapier';
import { useRef } from 'react';
import type { RosterEntry } from '@blackjack/shared';
import { HOUSE_ID } from '@blackjack/shared';
import { useGameStore } from '../state/store';
import { palette } from '../theme/tokens';
import {
  TABLE,
  TABLE_CENTER,
  CROUPIER,
  computeSeats,
  assignSeatIndices,
  PLAYER_SEAT_COUNT,
} from './seating';

/** Mulberry32 — tiny deterministic PRNG seeded from the server's integer seed. */
function mulberry32(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a |= 0;
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

const DIE = 0.18;

interface DieSpec {
  readonly id: string;
  readonly start: THREE.Vector3;
  readonly linvel: THREE.Vector3;
  readonly angvel: THREE.Vector3;
  readonly color: string;
}

function Die({ spec }: { readonly spec: DieSpec }) {
  const ref = useRef<RapierRigidBody>(null);
  return (
    <RigidBody
      ref={ref}
      colliders={false}
      position={[spec.start.x, spec.start.y, spec.start.z]}
      linearVelocity={[spec.linvel.x, spec.linvel.y, spec.linvel.z]}
      angularVelocity={[spec.angvel.x, spec.angvel.y, spec.angvel.z]}
      restitution={0.35}
      friction={0.9}
      linearDamping={0.4}
      angularDamping={0.5}
    >
      <CuboidCollider args={[DIE / 2, DIE / 2, DIE / 2]} />
      <mesh castShadow>
        <boxGeometry args={[DIE, DIE, DIE]} />
        <meshStandardMaterial color={spec.color} roughness={0.45} metalness={0.1} />
      </mesh>
    </RigidBody>
  );
}

/** Resolve each tied participant's seat floor position (house → croupier spot). */
function originFor(id: string, players: readonly RosterEntry[]): THREE.Vector3 {
  if (id === HOUSE_ID) return CROUPIER.floor.clone();
  const seats = computeSeats(PLAYER_SEAT_COUNT);
  const seatIndices = assignSeatIndices(players.length);
  const idx = players.findIndex((p) => p.id === id);
  const seat = idx >= 0 ? seats[seatIndices[idx] ?? idx] : undefined;
  return seat ? seat.floor.clone() : TABLE_CENTER.clone();
}

function DiceWorld({
  contestants,
  seed,
  players,
}: {
  readonly contestants: readonly string[];
  readonly seed: number;
  readonly players: readonly RosterEntry[];
}) {
  // Each die is tossed GENTLY from its owner's seat toward the table center, with a
  // deterministic seeded spin — slow enough to follow (low speed + damping).
  const specs = useMemo<DieSpec[]>(() => {
    const rng = mulberry32(seed);
    const colors = [palette.gold2, palette.velvet, '#2f6ab8', '#1f7a44', '#b83a3a', '#6a2fb8'];
    return contestants.map((id, i) => {
      const o = originFor(id, players);
      const start = new THREE.Vector3(o.x * 0.5, TABLE.topY + 0.7 + rng() * 0.2, o.z * 0.55 + TABLE_CENTER.z * 0.45);
      // Aim toward the felt center, gently.
      const dir = new THREE.Vector3(TABLE_CENTER.x - start.x, 0, TABLE_CENTER.z - start.z).normalize();
      const speed = 1.1 + rng() * 0.5;
      return {
        id,
        start,
        linvel: new THREE.Vector3(dir.x * speed, 0.4 + rng() * 0.3, dir.z * speed),
        angvel: new THREE.Vector3((rng() - 0.5) * 9, (rng() - 0.5) * 9, (rng() - 0.5) * 9),
        color: colors[i % colors.length]!,
      };
    });
  }, [contestants, seed, players]);

  return (
    <Physics timeStep={1 / 60} gravity={[0, -9.81, 0]} paused={false}>
      {/* Felt floor the dice land + settle on. */}
      <RigidBody type="fixed" colliders={false}>
        <CuboidCollider args={[2.8, 0.05, 1.6]} position={[0, TABLE.topY - 0.05, TABLE_CENTER.z]} />
      </RigidBody>
      {specs.map((spec) => (
        <Die key={spec.id} spec={spec} />
      ))}
    </Physics>
  );
}

/** Mounts the dice only during the tiebreak "throw" beat; re-keys per tiebreak. */
export function Dice() {
  const diceRolls = useGameStore((s) => s.server.diceRolls);
  const beat = useGameStore((s) => s.tiebreakBeat);
  const roster = useGameStore((s) => s.server.roster);
  const key = useGameStore((s) => s.diceAnimationKey);

  if (beat !== 'throw' || diceRolls.length === 0) return null;

  const players = roster.filter((r) => r.role === 'player');
  // All tied participants across both categories get a die from their seat.
  const contestants = diceRolls.flatMap((d) => d.contestants);
  const seed = diceRolls[0]!.seed;

  return <DiceWorld key={key} contestants={contestants} seed={seed} players={players} />;
}
